// src/components/subscription/CreditPurchaseSelection.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// import api from '../../utils/api';
// import { getAuthHeader } from '../../utils/authToken';

// import { CREDIT_PACKAGES } from '../../config/constants';
// import { formatCurrency } from '../../utils/currency';

import { CREDIT_PACKAGES } from './config/constants';
import { formatCurrency } from '../../../utils/currency';

import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  //   timeout: 5000,
  timeout: 30000, // Increase to 30 seconds
});

const token = localStorage.getItem('ownerToken');

const CreditPurchaseSelection = () => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [customCredits, setCustomCredits] = useState('');
  const [purchaseMode, setPurchaseMode] = useState('package');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Modified calculateTotal in CreditPurchaseCheckout:
  const calculateTotal = (type, amount, packageId) => {
    const currency = currentSubscription?.currency || 'usd';
    if (type === 'package' && packageId) {
      return CREDIT_PACKAGES[packageId].prices[currency].amount;
    } else if (type === 'custom' && amount) {
      return amount * 10; // $0.10 per credit in cents
    }
    return 0;
  };

  useEffect(() => {
    fetchCurrentSubscription();
  }, []);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/current', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to load subscription details');
    }
  };

  const handleCustomCreditsChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 10000)) {
      setCustomCredits(value);
      setError('');
    }
  };

  const calculatePrice = (credits) => {
    return credits * 10; // $0.10 per credit in cents
  };

  const handlePurchase = () => {
    if (purchaseMode === 'custom') {
      const amount = parseInt(customCredits);
      if (!amount || amount < 1 || amount > 10000) {
        setError('Please enter a valid amount between 1 and 10,000 credits');
        return;
      }
      navigate('/owner/test/credits/checkout', {
        state: {
          type: 'custom',
          amount,
          currency: currentSubscription?.currency || 'usd',
        },
      });
    } else if (selectedPackage) {
      navigate('/owner/test/credits/checkout', {
        state: {
          type: 'package',
          packageId: selectedPackage,
          package: CREDIT_PACKAGES[selectedPackage],
          currency: currentSubscription?.currency || 'usd',
        },
      });
    }
  };

  return (
    <div className='container mx-auto px-4 py-8 max-w-4xl'>
      <h2 className='text-2xl font-bold mb-6'>Purchase Additional Credits</h2>

      {/* Mode Selection */}
      <div className='bg-white rounded-lg shadow-md p-6 mb-8'>
        <div className='flex space-x-4 mb-6'>
          <button
            onClick={() => setPurchaseMode('package')}
            className={`flex-1 py-2 px-4 rounded-md text-center ${
              purchaseMode === 'package'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Credit Packages
          </button>
          <button
            onClick={() => setPurchaseMode('custom')}
            className={`flex-1 py-2 px-4 rounded-md text-center ${
              purchaseMode === 'custom'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Custom Amount
          </button>
        </div>

        {purchaseMode === 'package' ? (
          <div className='grid md:grid-cols-3 gap-6'>
            {Object.entries(CREDIT_PACKAGES).map(([id, pack]) => (
              <div
                key={id}
                className={`border rounded-lg p-6 hover:border-blue-500 cursor-pointer transition-all ${
                  selectedPackage === id
                    ? 'border-blue-500 shadow-lg'
                    : 'border-gray-200'
                }`}
                onClick={() => setSelectedPackage(id)}
              >
                <div className='flex justify-between items-start mb-4'>
                  <h3 className='text-xl font-bold'>{pack.name}</h3>
                  <span className='bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded'>
                    {pack.credits} Credits
                  </span>
                </div>
                <p className='text-gray-600 mb-4'>{pack.description}</p>
                <div className='flex justify-between items-center'>
                  <span className='text-2xl font-bold'>
                    {formatCurrency(
                      pack.prices[currentSubscription?.currency || 'usd']
                        .amount / 100
                    )}
                  </span>
                  <span className='text-sm text-gray-500'>
                    (
                    {formatCurrency(
                      pack.prices[currentSubscription?.currency || 'usd']
                        .amount /
                        pack.credits /
                        100
                    )}
                    /credit)
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='bg-gray-50 rounded-lg p-6'>
            <h3 className='text-lg font-bold mb-4'>Custom Amount</h3>
            <div className='flex gap-4'>
              <div className='flex-1'>
                <input
                  type='number'
                  value={customCredits}
                  onChange={handleCustomCreditsChange}
                  placeholder='Enter number of credits'
                  min='1'
                  max='10000'
                  className='w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                />
                {error && <p className='text-red-500 text-sm mt-1'>{error}</p>}
              </div>
              <button
                onClick={handlePurchase}
                disabled={!customCredits}
                className='px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Continue
              </button>
            </div>
            {customCredits && !error && (
              <p className='text-sm text-gray-600 mt-2'>
                Total:{' '}
                {formatCurrency(calculatePrice(parseInt(customCredits)) / 100)}{' '}
                ({formatCurrency(0.1)}/credit)
              </p>
            )}
          </div>
        )}
      </div>

      {purchaseMode === 'package' && (
        <div className='flex justify-end'>
          <button
            onClick={handlePurchase}
            disabled={!selectedPackage}
            className='bg-blue-500 text-white py-2 px-6 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Continue with Selected Package
          </button>
        </div>
      )}
    </div>
  );
};

export default CreditPurchaseSelection;
// // src/components/subscription/CreditPurchaseSelection.jsx
// import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// // import { CREDIT_PACKAGES } from '../../config/constants';
// // import { formatCurrency } from '../../utils/currency';

// import { CREDIT_PACKAGES } from './config/constants';
// import { formatCurrency } from '../../../utils/currency';

// const CreditPurchaseSelection = () => {
//   const [customAmount, setCustomAmount] = useState('');
//   const [error, setError] = useState('');
//   const navigate = useNavigate();

//   const handleCustomAmountChange = (e) => {
//     const value = e.target.value;
//     if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 10000)) {
//       setCustomAmount(value);
//       setError('');
//     }
//   };

//   const handlePackageSelect = (packageId) => {
//     navigate('/owner/test/credits/checkout', {
//       state: {
//         type: 'package',
//         packageId,
//         package: CREDIT_PACKAGES[packageId],
//       },
//     });
//   };

//   const handleCustomPurchase = () => {
//     const amount = parseInt(customAmount);
//     if (!amount || amount < 1 || amount > 10000) {
//       setError('Please enter a valid amount between 1 and 10,000 credits');
//       return;
//     }

//     navigate('/owner/test/credits/checkout', {
//       state: {
//         type: 'custom',
//         amount,
//       },
//     });
//   };

//   return (
//     <div className='container mx-auto px-4 py-8 max-w-4xl'>
//       <h2 className='text-2xl font-bold mb-6'>Purchase Additional Credits</h2>

//       {/* Credit Packages */}
//       <div className='grid md:grid-cols-3 gap-6 mb-8'>
//         {Object.entries(CREDIT_PACKAGES).map(([id, pkg]) => (
//           <div
//             key={id}
//             className='border rounded-lg p-6 hover:border-blue-500 cursor-pointer transition-all'
//             onClick={() => handlePackageSelect(id)}
//           >
//             <div className='flex justify-between items-start mb-4'>
//               <h3 className='text-xl font-bold'>{pkg.name}</h3>
//               <span className='bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded'>
//                 {pkg.credits} Credits
//               </span>
//             </div>
//             <p className='text-gray-600 mb-4'>{pkg.description}</p>
//             <div className='flex justify-between items-center'>
//               <span className='text-2xl font-bold'>
//                 {formatCurrency(pkg.prices.usd.amount / 100)}
//               </span>
//               <span className='text-sm text-gray-500'>
//                 ({formatCurrency(pkg.prices.usd.amount / pkg.credits / 100)}
//                 /credit)
//               </span>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* Custom Amount */}
//       <div className='bg-gray-50 rounded-lg p-6'>
//         <h3 className='text-lg font-bold mb-4'>Custom Amount</h3>
//         <div className='flex gap-4'>
//           <div className='flex-1'>
//             <input
//               type='number'
//               value={customAmount}
//               onChange={handleCustomAmountChange}
//               placeholder='Enter number of credits'
//               min='1'
//               max='10000'
//               className='w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
//             />
//             {error && <p className='text-red-500 text-sm mt-1'>{error}</p>}
//           </div>
//           <button
//             onClick={handleCustomPurchase}
//             disabled={!customAmount}
//             className='px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
//           >
//             Purchase
//           </button>
//         </div>
//         <p className='text-sm text-gray-600 mt-2'>
//           Price:{' '}
//           {customAmount
//             ? formatCurrency((parseInt(customAmount) * 10) / 100)
//             : '$0.00'}
//           ({formatCurrency(0.1)} per credit)
//         </p>
//       </div>
//     </div>
//   );
// };

// export default CreditPurchaseSelection;
